import { useAuth0 } from '@auth0/auth0-react';
import { OnActiveProjectSubscriptionChangedFunction, SendLogFunction } from 'functions/functionDeclarations';
import { Entities } from 'models/Entities';
import { ErrorCause, ErrorCodeCategory, ErrorResponse, isErrorResponse } from 'models/ErrorResponse';
import { FeatureLimitation } from 'models/FeatureLimitation';
import { HttpMethod } from 'models/HttpMethod';
import { LogLevel } from 'models/LogLevel';
import { ApiService } from 'services/ApiService';

export const useSubscriptionManager = (
  baseUrl: string,
  sendLog: SendLogFunction,
  onTokenExpired: () => void,
  onSelectedProjectChanged: OnActiveProjectSubscriptionChangedFunction,
  apiEndpoint: Entities,
) => {
  const { getAccessTokenSilently } = useAuth0();

  const subscriptionService = new ApiService<Entities.Subscriptions>(baseUrl, apiEndpoint as string);

  let accessToken: string;
  const setAccessToken = (token: string) => {
    accessToken = token;
    subscriptionService.setAccessToken(accessToken);
  };

  const obtainTokenSilently = async () => {
    const token = await getAccessTokenSilently();
    if (!token) {
      onTokenExpired();
      return;
    }
    subscriptionService.setAccessToken(token);
  };

  const getSubscriptionFeatureLimitations = async (companyId: number) => {
    await obtainTokenSilently();
    const response = await subscriptionService.execute(
      `FeatureLimitations?companyId=${companyId}`,
      HttpMethod.GET
    );

    if (response.ok) {
      const FeatureLimitations: FeatureLimitation[] = await response.json();
      onSelectedProjectChanged(FeatureLimitations, companyId);
      return FeatureLimitations;
    }

    if (response.status === ErrorCodeCategory.ClientError || response.status === ErrorCodeCategory.ServerError) {
      const errorMessage = await response.json();
      if (isErrorResponse(errorMessage)) {
        sendLog(`received error trying to get feature limits for company id ${companyId}: ${JSON.stringify(errorMessage)}`, LogLevel.error, errorMessage);
        onSelectedProjectChanged([], companyId, errorMessage);
        return [];
      }
    }

    const defaultError: ErrorResponse = {
      category: ErrorCodeCategory.Unknown,
      errorCodeNumber: -1,
      cause: ErrorCause.UnknownError,
      message: `unknown error while attempting to get feature limits for company id ${companyId}`
    };
    sendLog(`${defaultError.message} - status: ${response.status} - text: ${response.statusText}`, LogLevel.error);
    onSelectedProjectChanged([], companyId, defaultError);
    return [];
  };

  return {
    setAccessToken,
    getSubscriptionFeatureLimitations
  };
};
